// AdminPage — Phase 6 Task 8 cross-room 管理画面 (`/admin`).
//
// 役割:
//   1. cookie session で `GET /admin/rooms` を叩いてルーム一覧を取得.
//   2. 各行に「設定」(RoomSettingsModal を開く) と「削除」(soft-delete) を提供.
//   3. ヘッダにログイン中 email + ログアウトボタン.
//   4. 401 unauthorized は `/login` に navigate (cookie session 期限切れ).
//
// 着地経路:
//   - magic link 検証成功 (`ActionVerifySplash` の `navigate-admin` directive) 経由.
//   - LoginPage オーナーパスワード経路は直接 pathname `/<id>` に navigate するので
//     現時点で本ページには来ない.
//   - 別端末で AdminPage を開く: cookie session が無いと 401 → /login に流れる.
//
// 確定方針 (consult 2026-05-04 AK〜AS):
//   - AK. 行の「設定」ボタンで Modal、RoomSettingsModal 再利用 + credential タブ追加.
//   - AL. 削除 = シンプル confirm (1 step) + 赤色 destructive ボタン.
//   - AN. ログアウト → HomePage `/` (URL クエリ全クリア + reload で transport reset).
//   - AO. ヘッダに「ログイン中: <email>」表示 (email が localStorage にあれば).
//   - AQ. ソートは server default (`lastActiveAt 降順`) に従い、フィルタ UI は出さない.
//   - AR. 空欄ルーム名は `(無題)` フォールバック (`displayRoomName`).

import { useCallback, useEffect, useState } from 'react';

import {
  applyIssuedAdminTokens,
  displayRoomName,
  mapBindSessionEmailOutcome,
  mapDeleteRoomOutcome,
  mapListAdminRoomsOutcome,
  shouldNavigateToLogin,
  splitRoomsByEmailBinding,
} from '../application/adminPage.js';
import { entryFlagInitial } from '../application/entryVisibilityFields.js';
import type {
  ActiveRoomLookupResult,
  AdminRoomSummary,
  RoomAdminAuthOptions,
} from '../infrastructure/dispatcher/DispatcherClient.js';
import {
  type AdminEmailStore,
  createAdminEmailStore,
} from '../infrastructure/storage/AdminEmailStore.js';
import { useSession } from './hooks/useSession.js';
import { RoomSettingsModal } from './RoomSettingsModal.js';
import { hardReloadToHome, navigateToView } from './urlNavigation.js';

/* -----------------------------------------------------------------------------
 * URL navigation helpers (HomePage / LoginPage と同パターン)
 * --------------------------------------------------------------------------- */

// ログアウト後は WSClient / SessionProvider state も初期化したいので hard
// navigate (HomePage と同じ流儀の location.assign).
const goToHomeHard = (): void => {
  hardReloadToHome();
};

const goToLogin = (): void => {
  navigateToView('login');
};

/* -----------------------------------------------------------------------------
 * Email store (singleton — created once per page)
 * --------------------------------------------------------------------------- */

let emailStoreSingleton: AdminEmailStore | null = null;
const getEmailStore = (): AdminEmailStore => {
  if (emailStoreSingleton === null) emailStoreSingleton = createAdminEmailStore();
  return emailStoreSingleton;
};

/* -----------------------------------------------------------------------------
 * Page-level state
 * --------------------------------------------------------------------------- */

type LoadState =
  | { readonly t: 'loading' }
  | {
      readonly t: 'ok';
      /** owner_email == session.email のルーム (= 「メアド紐付け済」セクション). */
      readonly rooms: readonly AdminRoomSummary[];
      /**
       * localStorage に admin_token を持っているが owner_email がまだ session.email と
       * 一致していない active ルーム (= 「メアド未紐付け」セクション). 2026-05-23 W2
       * 公開前追加 — ワンクリック紐付け UX のための枠.
       */
      readonly unbound: readonly ActiveRoomLookupResult[];
    }
  | { readonly t: 'unauthorized' }
  | { readonly t: 'error'; readonly message: string };

/**
 * 削除確認ダイアログに渡す最小 shape. AdminRoomSummary と ActiveRoomLookupResult
 * の両方から作れるよう `{ id, name }` のみに正規化.
 */
interface PendingDeleteTarget {
  readonly id: string;
  readonly name: string;
}

export interface AdminPageProps {
  /** Override the email store (tests). Defaults to module singleton. */
  readonly emailStore?: AdminEmailStore;
}

export const AdminPage = ({ emailStore }: AdminPageProps = {}): React.ReactNode => {
  const { dispatcher, adminTokens } = useSession();
  const store = emailStore ?? getEmailStore();
  const [load, setLoad] = useState<LoadState>({ t: 'loading' });
  const [pendingDelete, setPendingDelete] = useState<PendingDeleteTarget | null>(null);
  const [openSettings, setOpenSettings] = useState<AdminRoomSummary | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  // 2026-05-23 W2 公開前追加: 「📧 メアドに紐付ける」ボタンの multi-click 防止.
  // 押下中の roomId を保持. 多重 click は anyPending で全行 disable する.
  const [pendingBindRoomId, setPendingBindRoomId] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoad({ t: 'loading' });
    // 一覧取得と admin_token 一括発行を並列実行. issueAdminTokens は
    // 「成功すれば bearer 経路を有効化する」副作用のみで、AdminPage の
    // 表示には影響させない (= 401/429/unknown はサイレントフェイル,
    // UI は listAdminRooms の結果に従う).
    //
    // 2026-05-23 W2 公開前追加: magic link で email 認証したユーザが
    // AdminPage を開いた時点で、自分が owner の全ルームの admin_token を
    // localStorage に保存する. 以降 ClassicLayout のヘッダ「設定」も含めて
    // 全画面で従来通り bearer 経路で動く.
    //
    // 2026-05-23 W2 公開前追加 (本変更): 「メアド未紐付けあなたのルーム」セクション
    // のための lookupRoomsByTokens も並列に叩く. **issueAdminTokens 完了後**
    // (= localStorage に bearer が落ちた後) の adminTokens.list() を使う必要が
    // あるので、`Promise.all` の **後段** で実行する 2 段構え.
    const [listOut, issueOut] = await Promise.all([
      dispatcher.listAdminRooms(),
      dispatcher.issueAdminTokens(),
    ]);
    applyIssuedAdminTokens(issueOut, adminTokens);
    // 401 / 429 / unknown はサイレントフェイル. listAdminRooms 側で 401 なら
    // /login に navigate する (= 認証期限切れ統一動線). issueAdminTokens
    // 単独で 401 なら cookie 自体は有効 (listAdminRooms 側も 401 になるはず)
    // で listAdminRooms 側の navigate に乗る. issueAdminTokens 単独 429 は
    // 「token は取れないが画面は動かす」UX に倒す (= AdminPage は cookie
    // session 経路だけで動かせる, bearer は credential 編集等のみ).

    const mapped = mapListAdminRoomsOutcome(listOut);
    if (shouldNavigateToLogin(mapped.outcome)) {
      setLoad({ t: 'unauthorized' });
      goToLogin();
      return;
    }
    if (mapped.outcome !== 'ok') {
      setLoad({
        t: 'error',
        message:
          'message' in mapped && mapped.message !== undefined
            ? mapped.message
            : 'エラーが発生しました。',
      });
      return;
    }

    // 未紐付け section 用に lookupRoomsByTokens を叩く. localStorage に
    // admin_token が 1 件も無いなら skip (= 全件「紐付け済」のみ).
    // fail-silent: 失敗時は未紐付け section は出さず、紐付け済のみ表示する
    // (= 致命的ではない).
    const tokenEntries = adminTokens.list();
    let unboundLookups: readonly ActiveRoomLookupResult[] = [];
    if (tokenEntries.length > 0) {
      const lookup = await dispatcher.lookupRoomsByTokens(
        tokenEntries.map((e) => ({ roomId: e.roomId, adminToken: e.token })),
      );
      if (lookup.outcome === 'ok') {
        // server が「rooms にも deleted_rooms にも該当しなかった」と判定した
        // roomId は localStorage から prune (= HomePage と同じ清掃ロジック).
        for (const roomId of lookup.invalid) {
          adminTokens.clear(roomId);
        }
        unboundLookups = lookup.active;
      } else {
        // eslint-disable-next-line no-console
        console.warn('[AdminPage] lookupRoomsByTokens failed:', lookup.outcome);
      }
    }
    const split = splitRoomsByEmailBinding(mapped.rooms, unboundLookups);
    setLoad({ t: 'ok', rooms: split.bound, unbound: split.unbound });
  }, [dispatcher, adminTokens]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onLogout = async (): Promise<void> => {
    // 失敗しても localStorage クリアは実施 (前段の cookie session が無効でも
    // ローカル状態は確実にクリアしたい).
    await dispatcher.logout();
    // 全 room の adminToken を消す. AdminTokenStore は per-room API しか持たない
    // ので、画面に出ている分は最低限消し、keys-prefix scan は localStorage 直接
    // 触りで実装 (AdminEmailStore の隣で完結させる).
    if (load.t === 'ok') {
      for (const r of load.rooms) {
        adminTokens.clear(r.id);
      }
    }
    // それでも他 room の token (画面外) が残る可能性があるので、prefix で総ナメ.
    if (typeof window !== 'undefined') {
      try {
        const ls = window.localStorage;
        const toRemove: string[] = [];
        for (let i = 0; i < ls.length; i++) {
          const k = ls.key(i);
          if (k?.startsWith('oekaki:adminToken:')) toRemove.push(k);
        }
        for (const k of toRemove) ls.removeItem(k);
      } catch {
        // private browsing / disabled storage — ignore.
      }
    }
    store.clear();
    goToHomeHard();
  };

  const onConfirmDelete = async (): Promise<void> => {
    const target = pendingDelete;
    if (target === null) return;
    setActionMessage(null);
    const adminToken = adminTokens.get(target.id);
    const auth: RoomAdminAuthOptions = adminToken !== null ? { adminToken } : {};
    const out = await dispatcher.deleteRoom(target.id, auth);
    const mapped = mapDeleteRoomOutcome(out);
    setPendingDelete(null);
    if (shouldNavigateToLogin(mapped.outcome)) {
      goToLogin();
      return;
    }
    if (mapped.outcome === 'ok') {
      // Remove from both lists (optimistic — server has soft-deleted).
      // 紐付け済 / 未紐付け の両セクションから消す.
      if (load.t === 'ok') {
        setLoad({
          t: 'ok',
          rooms: load.rooms.filter((r) => r.id !== target.id),
          unbound: load.unbound.filter((r) => r.roomId !== target.id),
        });
      }
      adminTokens.clear(target.id);
      setActionMessage(`ルーム「${displayRoomName(target.name)}」を削除しました。`);
      return;
    }
    setActionMessage(mapped.message ?? 'ルームの削除に失敗しました。');
  };

  /**
   * 「📧 メアドに紐付ける」ボタン押下時の handler.
   *
   * - 確認ダイアログなし (= 即実行、シンプル UX).
   * - 成功: refresh() → 紐付け済セクションに移動.
   * - 失敗: actionMessage に文言を出す. 401 は /login へ navigate.
   *
   * 並行多重 click は `pendingBindRoomId` で disable する.
   */
  const onBindEmail = async (room: ActiveRoomLookupResult): Promise<void> => {
    if (pendingBindRoomId !== null) return;
    setActionMessage(null);
    const adminToken = adminTokens.get(room.roomId);
    if (adminToken === null) {
      // localStorage から token が消えている (= 別タブで logout 等の race).
      // refresh() でセクションから自動で消えるはずだが、丁寧にメッセージを出す.
      setActionMessage(
        'ローカルの認証情報が見つかりません。ページを再読込してからお試しください。',
      );
      return;
    }
    setPendingBindRoomId(room.roomId);
    try {
      const out = await dispatcher.bindSessionEmail(room.roomId, { adminToken });
      const mapped = mapBindSessionEmailOutcome(out);
      if (shouldNavigateToLogin(mapped.outcome)) {
        goToLogin();
        return;
      }
      if (mapped.outcome === 'ok') {
        // 一覧再取得 (= 紐付け済セクションに自動で移動).
        await refresh();
        setActionMessage(
          `ルーム「${displayRoomName(room.name)}」を ${mapped.ownerEmail ?? ''} に紐付けました。`,
        );
        return;
      }
      setActionMessage(mapped.message);
    } finally {
      setPendingBindRoomId(null);
    }
  };

  /* -- render -- */
  return (
    <div className="min-h-full overflow-y-auto bg-[var(--color-bg)] p-4 text-[var(--color-text)]">
      <div className="mx-auto max-w-3xl">
        <Header email={store.get()} onLogout={onLogout} onGoHome={goToHomeHard} />

        {actionMessage !== null && (
          <p
            className="mb-3 rounded border border-white/10 bg-[var(--color-panel)] px-3 py-2 text-sm text-[var(--color-success)]"
            role="status"
          >
            {actionMessage}
          </p>
        )}

        <RoomList
          load={load}
          onRetry={() => {
            void refresh();
          }}
          onSettings={(r) => setOpenSettings(r)}
          onDelete={(r) => setPendingDelete(r)}
          onBindEmail={(r) => {
            void onBindEmail(r);
          }}
          pendingBindRoomId={pendingBindRoomId}
        />
      </div>

      {openSettings !== null && (
        <RoomSettingsModal
          roomId={openSettings.id}
          roomName={openSettings.name}
          isPublic={openSettings.isPublic}
          // Phase 7 Task 7-5: AdminRoomSummary は clearOpen を必ず持つ.
          clearOpen={openSettings.clearOpen}
          // entry-preview-members-2026-07-21: AdminRoomSummary.settings の実値を
          // undefined = On で boolean に解決してから渡す (= モーダル側は
          // 「undefined = 不明 → open 時 lookup」なので、こちらで確定させる).
          entryShowPreview={entryFlagInitial(openSettings.settings.entryShowPreview)}
          entryShowMembers={entryFlagInitial(openSettings.settings.entryShowMembers)}
          showCredentials
          onClose={() => {
            setOpenSettings(null);
            // refresh で server-side の name 変更等を取り直す.
            void refresh();
          }}
        />
      )}

      {pendingDelete !== null && (
        <DeleteRoomConfirm
          room={pendingDelete}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            void onConfirmDelete();
          }}
        />
      )}
    </div>
  );
};

/* -----------------------------------------------------------------------------
 * Sub-components
 * --------------------------------------------------------------------------- */

/**
 * 長い email を省略表示用に整形 (画面幅の狭い場合用、@ 前 8 字 + `…@<domain>`).
 * 24 字以内ならそのまま、超えたら省略.
 */
const truncateEmail = (email: string, max = 32): string => {
  if (email.length <= max) return email;
  const at = email.indexOf('@');
  if (at < 0) return `${email.slice(0, max - 1)}…`;
  const local = email.slice(0, at);
  const domain = email.slice(at);
  if (local.length <= 8) return `${local}${domain.slice(0, max - local.length - 1)}…`;
  return `${local.slice(0, 8)}…${domain}`;
};

const Header = ({
  email,
  onLogout,
  onGoHome,
}: {
  readonly email: string | null;
  readonly onLogout: () => Promise<void>;
  readonly onGoHome: () => void;
}): React.ReactNode => {
  const [logoutBusy, setLogoutBusy] = useState(false);
  return (
    <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-col">
        <h1 className="text-lg font-bold text-[var(--color-accent)]">管理画面</h1>
        {email !== null && (
          <p className="text-xs text-[var(--color-text-muted)]" title={email}>
            ログイン中: <span className="font-mono">{truncateEmail(email)}</span>
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onGoHome}
          className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-3 py-1 text-sm text-white transition hover:bg-[var(--color-bg)]"
        >
          ホームに戻る
        </button>
        <button
          type="button"
          disabled={logoutBusy}
          onClick={() => {
            setLogoutBusy(true);
            void onLogout().finally(() => setLogoutBusy(false));
          }}
          className="rounded bg-[var(--color-accent)] px-3 py-1 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:opacity-60"
        >
          {logoutBusy ? 'ログアウト中…' : 'ログアウト'}
        </button>
      </div>
    </header>
  );
};

const RoomList = ({
  load,
  onRetry,
  onSettings,
  onDelete,
  onBindEmail,
  pendingBindRoomId,
}: {
  readonly load: LoadState;
  readonly onRetry: () => void;
  readonly onSettings: (r: AdminRoomSummary) => void;
  readonly onDelete: (r: PendingDeleteTarget) => void;
  readonly onBindEmail: (r: ActiveRoomLookupResult) => void;
  readonly pendingBindRoomId: string | null;
}): React.ReactNode => {
  if (load.t === 'loading') {
    return (
      <div className="grid place-items-center py-10" role="status" aria-live="polite">
        <div className="flex flex-col items-center gap-2">
          <div
            className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent"
            aria-hidden="true"
          />
          <p className="text-sm text-[var(--color-text-muted)]">読み込み中…</p>
        </div>
      </div>
    );
  }
  if (load.t === 'unauthorized') {
    // navigate is queued; render a placeholder while we wait.
    return (
      <p className="py-10 text-center text-sm text-[var(--color-text-muted)]">
        ログインの有効期限が切れました。ログイン画面に移動します…
      </p>
    );
  }
  if (load.t === 'error') {
    return (
      <div className="rounded border border-[var(--color-error)]/40 bg-[var(--color-panel)] px-3 py-3">
        <p className="text-sm text-[var(--color-error)]" role="alert">
          {load.message}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 rounded border border-white/30 bg-[var(--color-bg-soft)] px-3 py-1 text-sm text-white hover:bg-[var(--color-bg)]"
        >
          再試行
        </button>
      </div>
    );
  }
  // 両セクションとも 0 件 → empty-state.
  if (load.rooms.length === 0 && load.unbound.length === 0) {
    return (
      <div className="rounded border border-white/10 bg-[var(--color-panel)] px-3 py-6 text-center">
        <p className="text-sm text-[var(--color-text-muted)]">
          所有しているルームはありません。
          <br />
          ホームから「ルームを作成」してください。
        </p>
      </div>
    );
  }
  // 2026-05-23 W2 公開前追加: 2 セクション分割表示.
  // 「メアド未紐付け」を **上** に置く (= メアド紐付け習慣化を促す導線、未紐付け
  // セクションが空のときは section 自体を出さない).
  return (
    <div className="flex flex-col gap-6">
      {load.unbound.length > 0 && (
        <section aria-label="メアド未紐付けあなたのルーム">
          <header className="mb-2 flex flex-col gap-1">
            <h2 className="font-bold text-[var(--color-text)] text-sm">
              メアド未紐付けあなたのルーム
              <span className="ml-2 rounded bg-[var(--color-accent)]/30 px-1.5 py-0.5 text-xs font-normal text-[var(--color-accent)]">
                {load.unbound.length}
              </span>
            </h2>
            <p className="text-xs text-[var(--color-text-muted)]">
              このメアドに紐付けると、別の端末からもこの一覧に表示されます。
            </p>
          </header>
          <ul className="flex flex-col gap-2">
            {load.unbound.map((r) => (
              <UnboundRoomRow
                key={r.roomId}
                room={r}
                pending={pendingBindRoomId === r.roomId}
                anyPending={pendingBindRoomId !== null}
                onBindEmail={onBindEmail}
                onDelete={onDelete}
              />
            ))}
          </ul>
        </section>
      )}
      <section aria-label="メアド紐付け済ルーム">
        <header className="mb-2">
          <h2 className="font-bold text-[var(--color-text)] text-sm">
            メアド紐付け済ルーム
            <span className="ml-2 rounded bg-white/10 px-1.5 py-0.5 text-xs font-normal text-[var(--color-text-muted)]">
              {load.rooms.length}
            </span>
          </h2>
        </header>
        {load.rooms.length === 0 ? (
          <div className="rounded border border-white/10 bg-[var(--color-panel)] px-3 py-4 text-center">
            <p className="text-xs text-[var(--color-text-muted)]">
              まだメアドに紐付けたルームはありません。
              <br />
              上の「📧 メアドに紐付ける」ボタンから紐付けできます。
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {load.rooms.map((r) => (
              <RoomRow key={r.id} room={r} onSettings={onSettings} onDelete={onDelete} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

const formatDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(
      2,
      '0',
    )}`;
  } catch {
    return iso;
  }
};

const RoomRow = ({
  room,
  onSettings,
  onDelete,
}: {
  readonly room: AdminRoomSummary;
  readonly onSettings: (r: AdminRoomSummary) => void;
  readonly onDelete: (r: PendingDeleteTarget) => void;
}): React.ReactNode => {
  const isDeleted = room.status === 'deleted';
  return (
    <li className="rounded border border-white/10 bg-[var(--color-panel)] p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate font-bold text-base text-[var(--color-text)]" title={room.name}>
            {displayRoomName(room.name)}
            {isDeleted && (
              <span className="ml-2 rounded bg-[var(--color-error)]/30 px-1.5 py-0.5 text-xs font-normal text-[var(--color-error)]">
                削除済
              </span>
            )}
            {room.isPublic && !isDeleted && (
              <span className="ml-2 rounded bg-[var(--color-accent)]/30 px-1.5 py-0.5 text-xs font-normal text-[var(--color-accent)]">
                公開
              </span>
            )}
            {room.ownerPasswordSet && (
              <span className="ml-2 rounded bg-white/10 px-1.5 py-0.5 text-xs font-normal text-[var(--color-text-muted)]">
                パスワード設定済
              </span>
            )}
          </p>
          <p className="mt-0.5 font-mono text-xs text-[var(--color-text-muted)]" title={room.id}>
            {room.id}
          </p>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            キャンバス: {room.settings.canvasWidth} × {room.settings.canvasHeight} ｜ 作成:{' '}
            {formatDate(room.createdAt)} ｜ 最終アクティブ: {formatDate(room.lastActiveAt)}
          </p>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <a
            href={`/${room.id}`}
            className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-3 py-1 text-sm text-white transition hover:bg-[var(--color-bg)]"
          >
            開く
          </a>
          <button
            type="button"
            disabled={isDeleted}
            onClick={() => onSettings(room)}
            className="rounded bg-[var(--color-accent)] px-3 py-1 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:opacity-50"
          >
            設定
          </button>
          <button
            type="button"
            disabled={isDeleted}
            onClick={() => onDelete({ id: room.id, name: room.name })}
            className="rounded bg-[var(--color-error)] px-3 py-1 text-sm font-bold text-white transition hover:brightness-110 disabled:opacity-50"
          >
            削除
          </button>
        </div>
      </div>
    </li>
  );
};

/**
 * 2026-05-23 W2 公開前追加: 「メアド未紐付け」セクション用の行.
 *
 * AdminRoomSummary より情報が少ない (= settings / createdAt / isPublic /
 * ownerPasswordSet / clearOpen / status を持たない `ActiveRoomLookupResult`) の
 * で、行内では name / id / lastActiveAt のみ表示する.
 *
 * 「📧 メアドに紐付ける」ボタンを最左に置き、押下中は disable + label 切替する.
 * 「設定」ボタンは未紐付けでも開きたい局面 (= password 設定等) があり得るが、
 * `AdminRoomSummary` shape を持っていないため本 W2 公開時点では非表示にする.
 * (= 紐付け後に紐付け済セクションに移動 → そこで「設定」を開く動線を推奨).
 *
 * Phase 7 Task 7-5 以降で「未紐付けでも設定を開きたい」要望が出たら、別途
 * `ActiveRoomLookupResult` から `AdminRoomSummary` 相当を取り直す pivot
 * endpoint を検討する.
 */
const UnboundRoomRow = ({
  room,
  pending,
  anyPending,
  onBindEmail,
  onDelete,
}: {
  readonly room: ActiveRoomLookupResult;
  /** この行の「紐付ける」が実行中. ボタン label を「紐付け中…」に. */
  readonly pending: boolean;
  /** 他行が実行中 (= 多重 click 防止). ボタンを disable に. */
  readonly anyPending: boolean;
  readonly onBindEmail: (r: ActiveRoomLookupResult) => void;
  readonly onDelete: (r: PendingDeleteTarget) => void;
}): React.ReactNode => {
  return (
    <li className="rounded border border-[var(--color-accent)]/30 bg-[var(--color-panel)] p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate font-bold text-base text-[var(--color-text)]" title={room.name}>
            {displayRoomName(room.name)}
            <span className="ml-2 rounded bg-[var(--color-accent)]/30 px-1.5 py-0.5 text-xs font-normal text-[var(--color-accent)]">
              未紐付け
            </span>
          </p>
          <p
            className="mt-0.5 font-mono text-xs text-[var(--color-text-muted)]"
            title={room.roomId}
          >
            {room.roomId}
          </p>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            最終アクティブ: {formatDate(room.lastActiveAt)}
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-wrap gap-2">
          <button
            type="button"
            disabled={anyPending}
            onClick={() => onBindEmail(room)}
            className="rounded bg-[var(--color-accent)] px-3 py-1 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:opacity-50"
            title="ログイン中のメアドにこのルームを紐付けます"
          >
            {pending ? '紐付け中…' : '📧 メアドに紐付ける'}
          </button>
          <a
            href={`/${room.roomId}`}
            className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-3 py-1 text-sm text-white transition hover:bg-[var(--color-bg)]"
          >
            開く
          </a>
          <button
            type="button"
            disabled={anyPending}
            onClick={() => onDelete({ id: room.roomId, name: room.name })}
            className="rounded bg-[var(--color-error)] px-3 py-1 text-sm font-bold text-white transition hover:brightness-110 disabled:opacity-50"
          >
            削除
          </button>
        </div>
      </div>
    </li>
  );
};

const DeleteRoomConfirm = ({
  room,
  onCancel,
  onConfirm,
}: {
  readonly room: PendingDeleteTarget;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}): React.ReactNode => {
  // Esc to cancel.
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [onCancel]);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="キャンセル"
        tabIndex={-1}
        onClick={onCancel}
        className="-z-10 absolute inset-0 cursor-default bg-black/60"
      />
      {/* w-full max-w: 固定 w-[440px] はスマホ縦 (390/360px) で親 p-4 を貫通して
          はみ出す (2026-07-03 一括点検、ErrorScreen と同パターン)。 */}
      <div className="w-full max-w-[440px] rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-bold text-[var(--color-error)]">ルームを削除</h2>
        <p className="mb-4 text-sm text-[var(--color-text)]">
          ルーム「<span className="font-bold">{displayRoomName(room.name)}</span>」を削除しますか？
          <br />
          描画・チャット履歴は失われます。
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-4 py-2 text-base text-white hover:bg-[var(--color-bg)]"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded bg-[var(--color-error)] px-4 py-2 text-base font-bold text-white transition hover:brightness-110"
          >
            削除
          </button>
        </div>
      </div>
    </div>
  );
};
